import React, { useState } from 'react'
import { BottomNav, ProgressHeader, TopBar } from './CarWithDriverShared'
import CarWithDriverStep3OneWay, { OneWayRouteEntry } from './CarWithDriverStep3OneWay'
import CarWithDriverStep3RoundTrip, { RoundTripEntry } from './CarWithDriverStep3RoundTrip'
import { useCwdLang } from './useCwdLang'

export interface Step3Data {
  services: ('round-trip' | 'one-way')[]
  roundTrip: RoundTripEntry[]
  oneWay: OneWayRouteEntry[]
}

interface Props {
  data: Step3Data
  availableCarTypes: string[]
  onDataChange: (d: Step3Data) => void
  currentStep: number
  totalSteps: number
  maxStep?: number
  onExit: () => void
  onBack: () => void
  onNext: (data: any) => Promise<void>
}

const CarWithDriverStep3: React.FC<Props> = ({
  data,
  availableCarTypes,
  onDataChange,
  currentStep,
  totalSteps,
  maxStep,
  onExit,
  onBack,
  onNext,
}) => {
  const { t } = useCwdLang()
  const [section, setSection] = useState<'service-select' | 'round-trip' | 'one-way'>(
    'service-select',
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const toggleService = (key: 'round-trip' | 'one-way') => {
    const current = data.services
    const next = current.includes(key) ? current.filter((s) => s !== key) : [...current, key]
    onDataChange({ ...data, services: next })
    setError('')
  }

  const handleServiceNext = () => {
    if (data.services.length === 0) {
      setError(t.step3.errNoService)
      return
    }
    setError('')
    setSection(data.services.includes('round-trip') ? 'round-trip' : 'one-way')
  }

  const handleRoundTripNext = (entries: RoundTripEntry[]) => {
    const updated: Step3Data = { ...data, roundTrip: entries }
    onDataChange(updated)
    if (data.services.includes('one-way')) {
      setSection('one-way')
    } else {
      setIsSubmitting(true)
      onNext(updated).finally(() => setIsSubmitting(false))
    }
  }

  const handleOneWayNext = async (routes: OneWayRouteEntry[]) => {
    const updated: Step3Data = { ...data, oneWay: routes }
    onDataChange(updated)
    setIsSubmitting(true)
    await onNext(updated)
    setIsSubmitting(false)
  }

  if (section === 'round-trip') {
    return (
      <CarWithDriverStep3RoundTrip
        entries={data.roundTrip}
        availableCarTypes={availableCarTypes}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onExit={onExit}
        onBack={() => setSection('service-select')}
        onNext={handleRoundTripNext}
        onBeforeExit={(entries) => onDataChange({ ...data, roundTrip: entries })}
        onManageCarTypes={onBack}
        submitting={isSubmitting}
      />
    )
  }

  if (section === 'one-way') {
    return (
      <CarWithDriverStep3OneWay
        routes={data.oneWay}
        availableCarTypes={availableCarTypes}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onExit={onExit}
        onBack={() =>
          setSection(data.services.includes('round-trip') ? 'round-trip' : 'service-select')
        }
        onNext={handleOneWayNext}
        onBeforeExit={(routes) => onDataChange({ ...data, oneWay: routes })}
        submitting={isSubmitting}
      />
    )
  }

  // ── Service select (checkboxes) ──────────────────────────────────────
  const stepProgress = (() => {
    const needsRT = data.services.includes('round-trip')
    const needsOW = data.services.includes('one-way')
    const hasRT = data.roundTrip.length > 0
    const hasOW = data.oneWay.length > 0
    if (needsRT && needsOW) return hasRT && hasOW ? 100 : hasRT || hasOW ? 50 : 0
    if (needsRT) return hasRT ? 100 : 0
    if (needsOW) return hasOW ? 100 : 0
    return 0
  })()

  const SERVICE_OPTIONS: {
    key: 'round-trip' | 'one-way'
    title: string
    desc: string
  }[] = [
    {
      key: 'round-trip',
      title: t.step3.roundTripTitle,
      desc: t.step3.roundTripDesc,
    },
    {
      key: 'one-way',
      title: t.step3.oneWayTitle,
      desc: t.step3.oneWayDesc,
    },
  ]

  return (
    <div
      style={{
        fontFamily: 'Poppins, sans-serif',
        backgroundColor: '#fff',
        minHeight: '100vh',
        maxWidth: '500px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <TopBar onExit={onExit} />

      <ProgressHeader
        currentStep={currentStep}
        totalSteps={totalSteps}
        maxStep={maxStep}
        title={t.step3.title}
        currentStepProgress={stepProgress}
      />

      <div style={{ flex: 1, padding: '0 16px', overflowY: 'auto' }}>
        <p style={{ fontSize: '14px', color: '#667085', margin: '0 0 16px', lineHeight: '1.5' }}>
          {t.step3.selectServiceDesc}
        </p>

        {SERVICE_OPTIONS.map(({ key, title, desc }) => {
          const checked = data.services.includes(key)
          return (
            <div
              key={key}
              onClick={() => toggleService(key)}
              style={{
                border: `2px solid ${checked ? '#0048B3' : '#D6DAE0'}`,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
                cursor: 'pointer',
                backgroundColor: checked ? '#EFF6FF' : '#fff',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              {/* Checkbox */}
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: `2px solid ${checked ? '#0048B3' : '#D6DAE0'}`,
                  backgroundColor: checked ? '#0048B3' : '#fff',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '2px',
                }}
              >
                {checked && (
                  <span
                    style={{ color: '#fff', fontSize: '12px', fontWeight: '700', lineHeight: 1 }}
                  >
                    ✓
                  </span>
                )}
              </div>
              <div>
                <p
                  style={{
                    margin: '0 0 4px',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#212427',
                  }}
                >
                  {title}
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#667085', lineHeight: '1.4' }}>
                  {desc}
                </p>
              </div>
            </div>
          )
        })}

        {error && <p style={{ color: '#ef4444', fontSize: '13px', margin: '4px 0' }}>{error}</p>}
      </div>

      <BottomNav
        onBack={onBack}
        onNext={handleServiceNext}
        nextDisabled={data.services.length === 0}
        loading={isSubmitting}
      />
    </div>
  )
}

export default CarWithDriverStep3
